/**
 * Recording that something happened, from anywhere in the app.
 *
 * Moved out of `features/analytics/index.js` on 27 Aug 2026, which is exactly
 * what that file's header said would happen: *"the moment a second screen
 * needs to emit an event, these twenty lines move to `lib/usage.js` and this
 * file imports them."* Features may not import each other (ARCHITECTURE.md
 * §3), and the Assistant is the second screen.
 *
 * `usage-model.js` came with it, from the same folder and for a stronger
 * reason: it is where every DECISION lives — what may be recorded about an
 * advisor, what Firestore will accept as a counter key, how `meta` is
 * trimmed — and a `lib/` module reaching into a feature to ask is worse than
 * the feature-to-feature import §3 actually forbids. Nothing in it changed.
 *
 * ── What this replaces ────────────────────────────────────────────────────
 *
 * `window.mbmTrack` (original.html:15573). Fire-and-forget behind a bare
 * `try` and a `.catch(function(){})`, on the stated principle that "analytics
 * must NEVER break the app". That principle is right and is kept. What is not
 * kept is being unable to find out: this returns a boolean, and the two silent
 * failure modes — a type Firestore would reject, and no signed-in uid — are
 * decided BEFORE anything is sent rather than thrown away inside a swallowed
 * promise.
 *
 * ── Why /v2 had none of this until now ────────────────────────────────────
 *
 * It was written on 16 Aug and never called. `trackUsage` had no call sites
 * anywhere outside its own tests, so `/insights` — which reads
 * `analytics_users.lastSeen` for "Active (7d)", `analytics_users.counts` for
 * Feature Usage, and `analytics_events` for the activity chart and every
 * Concierge panel — showed nothing at all for any advisor on `/v2`. The
 * dashboard was not broken. Nothing was feeding it.
 *
 * ── What is deliberately NOT ported ───────────────────────────────────────
 *
 * Legacy also emits `page_view` from a three-second poll (original.html's
 * "analytics v2" block), plus `field_edit` on every keystroke-committed field
 * and `search` on every search. Each event is TWO Firestore writes — the
 * event and the rollup — so that poll alone is on the order of 2,400 writes
 * an hour per signed-in advisor, for a number nobody would act on.
 *
 * The test applied on 27 Aug 2026, with Trey: *if this number were double
 * what I expected, would I do anything differently?* Sign-ins yes — that is
 * retention. Trips created yes — that is whether an advisor ever reached the
 * point of the product. Concierge yes — it costs money per message. Errors
 * and rage clicks yes — they are bugs. "Branding saved: 47", no.
 */

import { countUp, getUid, getUser, mergeDoc, serverTime, writeDoc } from './firebase.js'
import { isTrackableType, usageEvent, usageRollup } from './usage-model.js'

/**
 * Record one usage event.
 *
 * Never throws, never rejects, and never blocks the thing the advisor was
 * actually doing — every caller may ignore the returned promise. That is the
 * whole contract: a broken analytics pipeline must not be able to stop
 * somebody sending a message or saving a trip.
 *
 * @param {string} type - one of `EVENT_CATALOG`
 * @param {Record<string, any>} [meta]
 * @param {{agency?: string, nowMs?: number}} [options]
 * @returns {Promise<boolean>} whether the event was written
 */
export async function trackUsage(type, meta, options = {}) {
  if (!isTrackableType(type)) return false
  const uid = getUid()
  if (!uid) return false

  const user = getUser()
  const nowMs = Number.isFinite(options.nowMs) ? Number(options.nowMs) : Date.now()

  try {
    /* The id is generated here, exactly as `doc(collection(...))` generates it
       in the browser. `analytics_events` allows `create` and forbids `update`
       (firestore.rules:115-119), so a collision is REJECTED rather than
       overwriting somebody else's event. */
    const id = `ev_${nowMs.toString(36)}_${Math.random().toString(36).slice(2, 10)}`
    await writeDoc(['analytics_events', id], {
      ...usageEvent({ type, meta, user, agency: options.agency, nowMs }),
      ts: serverTime(),
    })
    await mergeDoc(['analytics_users', uid], {
      ...usageRollup({ user, agency: options.agency }),
      lastSeen: serverTime(),
      counts: { [type]: countUp(1) },
    })
    return true
  } catch (err) {
    /* Analytics never breaks the app. It does say so out loud. */
    console.warn('usage event not recorded', type, err)
    return false
  }
}

/**
 * Record an event and throw away the result.
 *
 * For call sites in the middle of doing something else, where awaiting a
 * write to an analytics collection would put a Firestore round trip between
 * an advisor's key press and the thing they pressed it for. The `.catch` is
 * belt and braces — `trackUsage` already catches — and exists so that a
 * future edit which lets something escape cannot produce an unhandled
 * rejection in an advisor's console.
 *
 * @param {string} type
 * @param {Record<string, any>} [meta]
 */
export function track(type, meta) {
  try {
    void trackUsage(type, meta).catch(() => {})
  } catch {
    /* Unreachable, and unreachable is where the last one lived too. */
  }
}
